import React from "react";
import { Box, Button, Chip, Divider } from "@mui/material";
import Image from "next/image";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import querystring from "querystring";
import { baseUrl } from "@/utils/api-definition";
import { IVoucher } from "@/types/voucherwallet";

interface VoucherActionsProps {
  voucherItem: IVoucher;
  handleOnClickDownload: () => void;
  handleModalTermsOpen: () => void;
}
const VoucherActions: React.FC<VoucherActionsProps> = ({
  voucherItem,
  handleOnClickDownload,
  handleModalTermsOpen,
}) => {
  const { i18n } = useLingui();

  const redeemedChipStyle = {
    backgroundColor: "#46bd46",
    color: "white",
    lineHeight: "20px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    padding: "1px 3px",
  };

  const buttonStyle = {
    textDecoration: "none",
    color: "inherit",
    fontWeight: 500,
    fontSize: "17px",
  };

  const renderRedeemedChip = () => (
    <Chip label={t(i18n)`REDEEMED`} sx={redeemedChipStyle} size="small" />
  );
  const renderLinkWithIcon = (icon: React.ReactNode, link: string, text: string) => {
    const fullLink =
      link.startsWith("http://") || link.startsWith("https://")
        ? `${link}`
        : link.startsWith("www")
          ? `https://${link}`
          : `${link}`;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
        {icon}
        <Button
          onClick={() => window.open(fullLink, "_blank", "noopener,noreferrer")}
          sx={{
            ...buttonStyle,
            "&:hover": {
              textDecoration: "none",
              color: "primary.main",
            },
            transition: "all 0.3s ease",
          }}
        >
          {text}
        </Button>
      </Box>
    );
  };

  const renderButtonWithIcon = (icon: React.ReactNode, onClick: () => void, text: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
      {icon}
      <Button
        onClick={onClick}
        sx={{
          ...buttonStyle,
          "&:hover": {
            textDecoration: "none",
            color: "primary.main",
          },
          transition: "all 0.3s ease",
        }}
      >
        {text}
      </Button>
    </Box>
  );
  let redeemLink = "";
  if (baseUrl && voucherItem.redeem_link?.includes(baseUrl)) {
    const voucherLink = {
      code: voucherItem.secret_code,
    };
    redeemLink = `/account/offerz-coupons?${querystring.stringify(voucherLink)}`;
  } else {
    redeemLink = voucherItem.redeem_link ?? "";
  }

  return (
    <Box sx={{ width: "100%" }}>
      {voucherItem.redeem_link && voucherItem.secret_code && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0,
              justifyContent: "space-between",
            }}
          >
            {renderLinkWithIcon(
              <Image src="/vtag.svg" alt="Logo" width={19} height={19} />,
              redeemLink,
              t(i18n)`Redeem Voucher`,
            )}
            {voucherItem.redeem_status === "1" && renderRedeemedChip()}
          </Box>
          <Divider />
        </>
      )}

      {voucherItem.pdf_link !== "" && voucherItem.pdf_link !== null && (
        <>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0, justifyContent: "space-between" }}
          >
            {renderButtonWithIcon(
              <Image src="/download-icon.svg" alt="Logo" width={19} height={19} />,
              handleOnClickDownload,
              t(i18n)`Show PDF Gift Card`,
            )}
            {voucherItem.redeem_status === "1" && !voucherItem.redeem_link && renderRedeemedChip()}
          </Box>
          <Divider />
        </>
      )}

      {voucherItem.create_gift_url && (
        <>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0, justifyContent: "space-between" }}
          >
            {renderButtonWithIcon(
              <Image src="/download-icon.svg" alt="Logo" width={19} height={19} />,
              handleOnClickDownload,
              t(i18n)`Download`,
            )}
            {voucherItem.redeem_status === "1" && !voucherItem.redeem_link && renderRedeemedChip()}
          </Box>
          <Divider />
        </>
      )}

      {voucherItem.jit_terms_and_cond_popup && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0, justifyContent: "space-between" }}
        >
          {renderButtonWithIcon(
            <Image src="/terms-conditions.svg" alt="Logo" width={19} height={19} />,
            handleModalTermsOpen,
            t(i18n)`Terms & Conditions`,
          )}
          {voucherItem.redeem_status === "1" &&
            !voucherItem.redeem_link &&
            !voucherItem.create_gift_url &&
            renderRedeemedChip()}
        </Box>
      )}
    </Box>
  );
};
export default VoucherActions;
